import { YAHOO_EDITABLE } from '../yahoo/yahooReview';
import { yahooBulkStagingError } from '../yahoo/yahooBulkEdit';
/**
 * Bulk-edit op staging — the frontend half of the Campaign Bulk Editor.
 *
 * The agent proposes; the backend previews and gates on user approval; then a
 * `bulk.staged` event arrives carrying a staging payload:
 *
 *   { edits:   { rowId: { field: value } },
 *     listOps: { rowId: { field: [{ op, value, newValue? }] } },
 *     rowOps:  [ { op: 'duplicate'|'delete'|'create', rowId?, templateRowId?, … } ] }
 *
 * This module's job is to fold that payload into the SAME pending-edit state a
 * manual grid edit uses (useReviewGridEdits' editedRows / pendingListOps), so
 * agent edits and hand edits are literally the same thing from there on: same
 * appliers (applyLauncherResultEdits / applyGtConfigEdits / applyCopyReviewEdits),
 * same save, same sanity checks, same audit trail.
 *
 * IT ALSO RE-VALIDATES. The backend already checked every op against the
 * platform's editable set, and this checks again — deliberately, because the
 * editable set lives here and this is the last point before a value lands in the
 * state the appliers read. The precedent is explicit in the code this integrates
 * with: `entityStatus` is denylisted on every platform AND refused inside
 * applyTtdFields, because "the denylist is not the only way a field reaches this
 * applier" (reviewEdits.js:165). A staged agent edit is exactly such a way.
 */

import {
  DV360_EDITABLE,
  TTD_EDITABLE,
  GT_EDITABLE,
} from '../clwGridConstants';
import { gadsEditableFields } from '../gads/gadsConstants';
import { GT_CAMPAIGN_ROW_ID } from '../reviewEdits';

/**
 * Fields no surface may ever write, mirroring services/bulkEdit/fieldMap.js's
 * ALWAYS_REFUSED. Activation is the Launch action's job: TTD has no
 * creator-side pause backstop, so a status write rides straight into
 * POST /adgroup and puts a plan live and bidding.
 */
export const NEVER_STAGEABLE = new Set(['entityStatus', 'status']);

/**
 * geo's writable halves are sub-fields of the `geo` cell and are not members of
 * the editable set themselves — they inherit its editability, the same way
 * CellDetailPanel gates them on `geoEditable`.
 */
const SUB_FIELD_PARENT = {
  geoInclude: 'geo',
  geoExclude: 'geo',
};

const editableKeyFor = (field) => SUB_FIELD_PARENT[field] || field;

/**
 * The editable set for a surface + platform.
 *
 * `copyEditableFor` is imported lazily by the caller and passed in as
 * `copyEditable` rather than imported here, so this module stays free of a
 * dependency on the copy workflow (which would make CLW's bundle pull it in).
 */
export function resolveEditableFields({ platform, surface, gadsSubtype, copyEditable }) {
  if (surface === 'copy') {
    // copyEditableFor speaks families: 'ttd' | 'dv360' | 'gads'.
    const family = platform === 'groundtruth' ? 'dv360' : platform;
    return copyEditable?.(family, gadsSubtype)?.editableFields || new Set();
  }
  if (platform === 'yahoo') return YAHOO_EDITABLE;
  if (platform === 'ttd') return TTD_EDITABLE;
  if (platform === 'dv360') return DV360_EDITABLE;
  if (platform === 'groundtruth') return GT_EDITABLE;
  if (platform === 'gads') return gadsEditableFields(gadsSubtype || 'search');
  return new Set();
}

/**
 * Validate a staged batch against the grid's own rules.
 *
 * @returns {{ edits, listOps, rowOps, refused, accepted }}
 *   `refused` entries are surfaced to the user — never dropped silently, because
 *   a bulk edit that quietly applies to 40 of 42 rows is worse than one that
 *   says which two it skipped.
 *
 * LAUNCH STATE IS NOT CHECKED. Bulk editing is available in every state. Field
 * editability still is — a read-only FIELD has no writer and would silently
 * vanish, which is a different problem from a plan being live. (The manual cell
 * editor keeps its own read-only-after-launch behaviour; that is pre-existing and
 * unrelated to this path.)
 */
export function validateStaging(staging, {
  editableFields, listFields, knownRowIds,
  platform, reviewRows, selectedRowIds, alreadyLaunched,
  // Campaign-scope keys (the GT campaign-configuration card). Edits under the
  // GT_CAMPAIGN_ROW_ID sentinel validate against THIS set, not the row set —
  // hosts that don't pass it (LaunchCampaignDetails) refuse the sentinel.
  campaignFields,
} = {}) {
  if (platform === 'yahoo') {
    const error = yahooBulkStagingError(staging, { reviewRows, selectedRowIds, alreadyLaunched });
    if (error) return { edits: {}, listOps: {}, rowOps: [], refused: [{ reason: error }], accepted: 0 };
  }
  const refused = [];
  const edits = {};
  const listOps = {};
  const rowOps = [];

  const editable = editableFields instanceof Set ? editableFields : new Set(editableFields || []);
  const caps = listFields || {};
  // Our op vocabulary → the flag the grid config uses.
  const LIST_OP_FLAG = { add: 'add', remove: 'remove', edit: 'edit' };
  const known = knownRowIds instanceof Set
    ? knownRowIds
    : (Array.isArray(knownRowIds) ? new Set(knownRowIds.map(String)) : null);

  const rowKnown = (rowId) => !known || known.has(String(rowId));
  const fieldOk = (rowId, field) => {
    if (NEVER_STAGEABLE.has(field)) {
      refused.push({ rowId, field, reason: `${field} is never editable from a review grid — activation is the Launch action's job.` });
      return false;
    }
    if (editable.size && !editable.has(editableKeyFor(field))) {
      refused.push({ rowId, field, reason: `${field} is read-only on this platform.` });
      return false;
    }
    return true;
  };

  const campaignEditable = campaignFields instanceof Set
    ? campaignFields
    : new Set(campaignFields || []);

  let accepted = 0;

  for (const [rowId, fields] of Object.entries(staging?.edits || {})) {
    // Campaign-scope edits: not a grid row, so skip the rowKnown check and
    // validate against the campaign key set instead of the row editable set.
    if (rowId === GT_CAMPAIGN_ROW_ID) {
      for (const [field, value] of Object.entries(fields || {})) {
        if (!campaignEditable.has(field)) {
          refused.push({ rowId, field, reason: `${field} is not an editable campaign setting on this surface.` });
          continue;
        }
        if (!edits[rowId]) edits[rowId] = {};
        edits[rowId][field] = value;
        accepted += 1;
      }
      continue;
    }
    if (!rowKnown(rowId)) {
      refused.push({ rowId, reason: 'That row is not in the grid any more — ask the agent to re-read the plan.' });
      continue;
    }
    for (const [field, value] of Object.entries(fields || {})) {
      if (!fieldOk(rowId, field)) continue;
      if (!edits[rowId]) edits[rowId] = {};
      edits[rowId][field] = value;
      accepted += 1;
    }
  }

  for (const [rowId, fields] of Object.entries(staging?.listOps || {})) {
    if (!rowKnown(rowId)) {
      refused.push({ rowId, reason: 'That row is not in the grid any more — ask the agent to re-read the plan.' });
      continue;
    }
    for (const [field, ops] of Object.entries(fields || {})) {
      if (NEVER_STAGEABLE.has(field)) {
        refused.push({ rowId, field, reason: `${field} is never editable from a review grid.` });
        continue;
      }
      const clean = (Array.isArray(ops) ? ops : []).filter(
        (o) => o && o.op && o.value !== undefined && o.value !== null && o.value !== '',
      );
      if (!clean.length) continue;
      // A LIST op is gated on the list-field capability config — the same source
      // CellDetailPanel gates its per-item controls on — NOT on the scalar
      // editable set. A field can be absent from that set (no scalar writer) and
      // still grant add/remove: Copy TTD `geo` is exactly that, and checking the
      // scalar set is what made the agent refuse geo on a grid where a human
      // edits it by hand. When no capability map was supplied, fall back to the
      // scalar set so older callers keep their previous behaviour.
      const cfg = Object.keys(caps).length ? caps[field] : null;
      const granted = clean.filter((o) => {
        if (cfg) return Boolean(cfg.ops?.[LIST_OP_FLAG[o.op] || o.op]);
        return fieldOk(rowId, field);
      });
      if (granted.length !== clean.length) {
        refused.push({
          rowId,
          field,
          reason: cfg?.reason
            || `${field} does not accept every requested operation on this platform.`,
        });
      }
      if (!granted.length) continue;
      if (!listOps[rowId]) listOps[rowId] = {};
      listOps[rowId][field] = granted;
      accepted += granted.length;
    }
  }

  for (const op of (staging?.rowOps || [])) {
    if (!op?.op) continue;
    if (op.op === 'create') {
      if (!rowKnown(op.templateRowId)) {
        refused.push({ rowId: op.templateRowId, reason: 'The template row is not in the grid any more.' });
        continue;
      }
      const overrides = {};
      for (const [field, value] of Object.entries(op.overrides || {})) {
        if (!fieldOk(op.templateRowId, field)) continue;
        overrides[field] = value;
      }
      rowOps.push({ ...op, overrides });
      accepted += 1;
      continue;
    }
    if (!rowKnown(op.rowId)) {
      refused.push({ rowId: op.rowId, reason: 'That row is not in the grid any more.' });
      continue;
    }
    rowOps.push(op);
    accepted += 1;
  }

  return { edits, listOps, rowOps, refused, accepted };
}

/**
 * Merge validated scalar edits into an existing editedRows map.
 * Agent edits and manual edits share the map, so a later value wins — the same
 * last-write-wins the grid already has when a user retypes a cell.
 */
export function mergeEdits(prev, edits) {
  if (!edits || !Object.keys(edits).length) return prev;
  const next = { ...prev };
  for (const [rowId, fields] of Object.entries(edits)) {
    next[rowId] = { ...(next[rowId] || {}), ...fields };
  }
  return next;
}

/**
 * Merge validated list ops into an existing pendingListOps map.
 *
 * Ops APPEND rather than replace, matching the panel's staging model, but an op
 * that already exists for the same (field, op, value) is not duplicated —
 * otherwise re-confirming a similar batch would stage "remove Houston" twice and
 * the applier would try to remove an entry that is already gone.
 */
export function mergeListOps(prev, listOps) {
  if (!listOps || !Object.keys(listOps).length) return prev;
  const next = { ...prev };
  for (const [rowId, fields] of Object.entries(listOps)) {
    const row = { ...(next[rowId] || {}) };
    for (const [field, ops] of Object.entries(fields)) {
      const existing = [...(row[field] || [])];
      for (const op of ops) {
        const dupe = existing.some(
          (e) => e.op === op.op && e.value === op.value && (e.newValue ?? null) === (op.newValue ?? null),
        );
        if (!dupe) existing.push(op);
      }
      row[field] = existing;
    }
    next[rowId] = row;
  }
  return next;
}

/**
 * Merge validated row ops, de-duplicating identical entries.
 *
 * A `create` is only a duplicate when its WHOLE payload matches — count and
 * overrides included. Keying on (op, templateRowId) alone ate every repeat
 * clone batch from the same template rows: "clone PKG 1 as PKG 3" then
 * "clone PKG 1 as PKG 4" silently produced one set, not two (UAT 0819).
 * Re-confirming the SAME batch still dedupes.
 */
const canonicalOverrides = (o) => JSON.stringify(o || {}, Object.keys(o || {}).sort());

export function mergeRowOps(prev, rowOps) {
  if (!rowOps || !rowOps.length) return prev;
  const next = [...(prev || [])];
  for (const op of rowOps) {
    const dupe = next.some(
      (e) => e.op === op.op
        && (e.rowId ?? null) === (op.rowId ?? null)
        && (e.templateRowId ?? null) === (op.templateRowId ?? null)
        && (op.op !== 'create'
          || ((e.count ?? 1) === (op.count ?? 1)
            // Canonical form: JSON key order follows the agent's tool-call
            // JSON, which nothing guarantees is stable across repeat calls.
            && canonicalOverrides(e.overrides) === canonicalOverrides(op.overrides))),
    );
    if (!dupe) next.push(op);
  }
  return next;
}

/** Human-readable count for the "staged N edits" line. */
export function countStaged({ edits, listOps, rowOps }) {
  let n = 0;
  for (const fields of Object.values(edits || {})) n += Object.keys(fields || {}).length;
  for (const fields of Object.values(listOps || {})) {
    for (const ops of Object.values(fields || {})) n += (ops?.length || 0);
  }
  n += (rowOps || []).length;
  return n;
}
